// SB6-ANA-POP-02 — pop-out window body. A pop-out window is identified by
// `?popout=1&windowId=<id>` and renders the grid of widgets the workspace
// store has assigned to that window (Phase 3). A single-widget window fills
// the viewport (the Phase-1 Viewer experience); multiple widgets render in a
// grid (`PopoutWidgetGrid`).
//
// Lifecycle:
//   - The window boots with the workspace state in its (copied) sessionStorage
//     and stays in sync via `useWorkspaceSync`.
//   - If its `windowId` later vanishes from the workspace (all its widgets were
//     moved/brought back elsewhere, or it was closed), the window self-closes.
//   - On OS-chrome close, it broadcasts the workspace minus itself so the main
//     window returns its widgets to the dashboard.
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import {
  usePopoutWorkspaceStore,
  type PopoutWindowState,
} from "./popoutWorkspaceStore.js";
import { createSyncChannel } from "./popoutSync.js";
import { PopoutWidgetGrid } from "./PopoutWidgetGrid.js";
import { type PopoutWidgetKey } from "./windowSizes.js";

/** Parse the pop-out window parameters, or null if this isn't a pop-out. */
export function readPopoutParams(
  search: URLSearchParams,
): { windowId: string } | null {
  if (search.get("popout") !== "1") return null;
  const windowId = search.get("windowId");
  if (!windowId) return null;
  return { windowId };
}

export interface PopoutHostProps {
  inspectionId: string;
  /** Render-prop for a widget body — the analysis feature injects the actual
   *  JSX so this host stays widget-agnostic. */
  readonly renderWidget: (widget: PopoutWidgetKey) => React.ReactNode;
}

export function PopoutHost({
  inspectionId,
  renderWidget,
}: PopoutHostProps): React.ReactElement | null {
  const [search] = useSearchParams();
  const params = readPopoutParams(search);
  const windowId = params?.windowId ?? null;
  const win = usePopoutWorkspaceStore((s) =>
    windowId ? (s.windows.find((w) => w.windowId === windowId) ?? null) : null,
  );

  // Self-close once our window disappears from the workspace (its widgets were
  // all moved/brought back, or it was closed from elsewhere). Guarded by
  // `seenRef` so we don't close during the initial hydrate/sync tick.
  const seenRef = useRef(false);
  useEffect(() => {
    if (!windowId) return;
    if (win) {
      seenRef.current = true;
      return;
    }
    if (seenRef.current) {
      try {
        window.close();
      } catch {
        /* some browsers refuse to close non-script-opened windows */
      }
    }
  }, [windowId, win]);

  // On OS-chrome close, hand our widgets back to the main window.
  useEffect(() => {
    if (!windowId) return;
    const onUnload = (): void => {
      try {
        const s = usePopoutWorkspaceStore.getState();
        if (!s.windows.some((w) => w.windowId === windowId)) return;
        const next: PopoutWindowState[] = s.windows.filter(
          (w) => w.windowId !== windowId,
        );
        const ch = createSyncChannel({ dataAcquisitionId: inspectionId });
        ch.postMessage({
          type: "workspace",
          version: Date.now(),
          windows: next,
        });
      } catch {
        /* best-effort */
      }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.removeEventListener("beforeunload", onUnload);
    };
  }, [windowId, inspectionId]);

  if (!params || !win) return null;
  return (
    <Box
      role="region"
      aria-label="Pop-out window"
      data-popout-host="true"
      data-popout-window-id={win.windowId}
      sx={{
        position: "fixed",
        inset: 0,
        overflow: "hidden",
        bgcolor: "background.default",
      }}
    >
      <PopoutWidgetGrid
        windowId={win.windowId}
        layout={win.layout}
        renderWidget={renderWidget}
      />
    </Box>
  );
}
